import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  MovementCalculator,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { LegacyBoilerWallsSurface3D } from 'features/assetViz/objects/surfaces/LegacyBoilerWallsSurface3D'
import { SceneFactoryConfig } from 'features/assetViz/scenes/sceneFactory'
import React from 'react'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoEditableComponent,
  GeckoLegacyBoilerWalls,
  GeckoMeasurementUnit,
  TUBESPACING_DEFAULT,
} from 'types'

export const LegacyBoilerSceneConfig: SceneFactoryConfig = {
  name: 'LegacyBoiler',
  assetModels: [GeckoAssetModelType.LegacyBoiler],
  componentModels: [GeckoComponentModelType.LegacyBoilerWalls],
  sceneFunc: (props) => <LegacyBoilerScene {...props} />,
}

/**
 * A scene depicting a boiler with waterwalls.
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 * @constructor
 */
export const LegacyBoilerScene: React.FC<{
  componentModel: GeckoEditableComponent
}> = ({ componentModel }) => {
  const selectedModel = componentModel as GeckoLegacyBoilerWalls

  //----------------------------------------------------------------------------
  const { componentDataFrames } = useAssetVizInternal()

  const tubeSpacing = React.useMemo(
    () => selectedModel.tubeSpacing ?? TUBESPACING_DEFAULT,
    [selectedModel]
  )

  const componentBounds = React.useMemo(() => {
    const componentBounds = selectedModel.componentBounds
    if (
      componentBounds.x.unit !== GeckoMeasurementUnit.Tubes ||
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters
    )
      throw new Error(
        `Legacy Boilers in the asset model should always be in tubes and meters,
        but the x range is in ${componentBounds.x.unit} and y range is in
        ${componentBounds.y.unit}`
      )
    return componentBounds
  }, [selectedModel.componentBounds])

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      // This starts the camera off wherever there's data to look at
      componentDataFrames
        ?.at(0)
        ?.data?.geometry?.project(componentBounds.space) ?? componentBounds,
    [componentDataFrames, componentBounds]
  )

  const getMovementForCursorDelta = React.useCallback<MovementCalculator>(
    ([x, y]) => [x / tubeSpacing.num, y],
    [tubeSpacing]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({
      rect: componentBounds,
    }),
    [componentBounds]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relY = componentY - componentBounds.start.y.num
      const relX = tubeSpacing.num * (componentX - componentBounds.start.x.num)
      return {
        position: [relX, relY, distToComponent],
        rotation: [0, 0, 0],
      }
    },
    [tubeSpacing, componentBounds]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) => [rect.width.num * tubeSpacing.num, rect.height.num],
    [tubeSpacing]
  )

  //----------------------------------------------------------------------------

  return !selectedModel ? null : (
    <>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getMovementForCursorDelta={getMovementForCursorDelta}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {selectedModel instanceof GeckoLegacyBoilerWalls ? (
        <LegacyBoilerWallsSurface3D model={selectedModel} />
      ) : null}
    </>
  )
}
